import { useCallback, useState } from 'react';
import { useAccount, usePublicClient, useWalletClient, useSwitchChain, useConfig } from 'wagmi';
import { getPublicClient } from '@wagmi/core';
import { decodeChainSwapRequested } from '../lib/bridgeCalldata';

// useBridgeSubmit
//
// Orchestrates the two on-chain transactions the customer signs directly:
//   1. approve      → ERC20 approve for srcBridgeSenderAddress on srcKStableAddress
//   2. requestSwap  → the actual bridge burn on srcBridgeSenderAddress
//
// Both calls use the customer's wallet (Wagmi walletClient.sendTransaction)
// with calldata built client-side in lib/bridgeCalldata.js — this app never
// asks a server to build or hold the transaction. After each, we wait for the
// receipt via the publicClient.
//
// State machine: idle → approving → approved → burning → burned | error
//
// Once the burn is mined we decode the ChainSwapRequested event straight out
// of the receipt to recover `swapId` (the contract derives it from
// block.number at execution time, so it cannot be predicted client-side
// ahead of the tx) plus the exact on-chain `netAmount` — this is what
// useBridgeDelivery polls for on the destination chain (trustless, no
// server-side status store).

export const SUBMIT_STATES = {
  IDLE: 'idle',
  APPROVING: 'approving',
  APPROVED: 'approved',
  BURNING: 'burning',
  BURNED: 'burned',
  ERROR: 'error',
};

export default function useBridgeSubmit() {
  const { chainId } = useAccount();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  const { switchChainAsync } = useSwitchChain();
  const config = useConfig();
  const [state, setState] = useState(SUBMIT_STATES.IDLE);
  const [approveTxHash, setApproveTxHash] = useState(null);
  const [burnTxHash, setBurnTxHash] = useState(null);
  const [burnConfirmedAt, setBurnConfirmedAt] = useState(null);
  const [swapId, setSwapId] = useState(null);
  const [netAmountWei, setNetAmountWei] = useState(null);
  const [error, setError] = useState(null);

  const run = useCallback(
    async ({
      srcChainId,
      srcKStableAddress,
      srcBridgeSenderAddress,
      approvalCalldata,
      bridgeCalldata,
    }) => {
      if (!walletClient || !publicClient) {
        setError('Wallet not ready.');
        setState(SUBMIT_STATES.ERROR);
        return;
      }
      try {
        setError(null);
        // HARD SOURCE-CHAIN GUARD (2026-07-15): the burn/approve MUST land on the
        // source chain. Without this, a wallet sitting on the wrong network (e.g.
        // Ethereum) signs the approve there — a no-op for us — and the flow times
        // out waiting for it on the source-chain publicClient. Force the switch
        // first, and pass `chainId` to every sendTransaction so viem refuses to
        // broadcast on any other chain (belt-and-suspenders — the walletClient
        // closure can lag the switch).
        if (srcChainId && chainId !== srcChainId) {
          setState(SUBMIT_STATES.APPROVING);
          try {
            await switchChainAsync({ chainId: srcChainId });
          } catch (sw) {
            throw new Error(
              `Please switch your wallet to the source chain (id ${srcChainId}) and retry` +
                (sw?.shortMessage ? ` — ${sw.shortMessage}` : '') + '.',
            );
          }
        }
        // Receipt waits MUST use the source-chain client — usePublicClient() (and
        // the captured `publicClient`) follows whatever chain the wallet was on
        // when run() was created, which after a switch is stale. Pin it to srcChainId.
        const srcPublicClient =
          (srcChainId && getPublicClient(config, { chainId: srcChainId })) || publicClient;

        setState(SUBMIT_STATES.APPROVING);
        const approveHash = await walletClient.sendTransaction({
          chainId: srcChainId,
          to: srcKStableAddress,
          data: approvalCalldata,
          value: 0n,
        });
        setApproveTxHash(approveHash);
        // viem's waitForTransactionReceipt resolves even for a REVERTED tx —
        // it only throws on RPC-level failure/timeout. Money-path rule (no
        // silent failures): check receipt.status explicitly, never assume a
        // resolved promise means success.
        const approveReceipt = await srcPublicClient.waitForTransactionReceipt({ hash: approveHash });
        if (approveReceipt.status !== 'success') {
          throw new Error(`Approve transaction reverted on-chain (${approveHash}).`);
        }
        setState(SUBMIT_STATES.APPROVED);

        setState(SUBMIT_STATES.BURNING);
        const burnHash = await walletClient.sendTransaction({
          chainId: srcChainId,
          to: srcBridgeSenderAddress,
          data: bridgeCalldata,
          value: 0n,
        });
        // Persisted by the caller (BridgeStatusPage) the moment burnTxHash is
        // set, BEFORE we know success/revert — this is what makes a mid-flight
        // page reload safe: the next mount sees a persisted hash and recovers
        // from the receipt instead of re-signing a second requestSwap.
        setBurnTxHash(burnHash);
        const receipt = await srcPublicClient.waitForTransactionReceipt({ hash: burnHash });
        if (receipt.status !== 'success') {
          throw new Error(
            `Burn transaction reverted on-chain (${burnHash}) — no funds were bridged. ` +
              'Start a new bridge from the landing page.',
          );
        }
        setBurnConfirmedAt(Math.floor(Date.now() / 1000));

        // Decode ChainSwapRequested straight out of the receipt — this is
        // the ONLY reliable source for swapId (derived on-chain from
        // block.number, which we cannot know ahead of the broadcast).
        const args = decodeChainSwapRequested(receipt, srcBridgeSenderAddress);
        if (args) {
          setSwapId(args.swapId);
          setNetAmountWei(args.netAmount);
        }

        setState(SUBMIT_STATES.BURNED);
        return { approveHash, burnHash, swapId: args?.swapId, netAmountWei: args?.netAmount };
      } catch (e) {
        setError(e?.shortMessage || e?.message || 'Transaction failed.');
        setState(SUBMIT_STATES.ERROR);
        return null;
      }
    },
    [walletClient, publicClient, chainId, switchChainAsync, config],
  );

  const reset = useCallback(() => {
    setState(SUBMIT_STATES.IDLE);
    setApproveTxHash(null);
    setBurnTxHash(null);
    setBurnConfirmedAt(null);
    setSwapId(null);
    setNetAmountWei(null);
    setError(null);
  }, []);

  return {
    run,
    reset,
    state,
    approveTxHash,
    burnTxHash,
    burnConfirmedAt,
    swapId,
    netAmountWei,
    error,
    isBusy:
      state === SUBMIT_STATES.APPROVING ||
      state === SUBMIT_STATES.APPROVED ||
      state === SUBMIT_STATES.BURNING,
  };
}
